import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { safeBrandingUrl } from './TenantBrand';
import '../styles/panel-theme.css';
import './customer-reference-shell.css';

function navigationTarget(value) {
  if (typeof value !== 'string' || !value || value.length > 2048 || /[\\\u0000-\u0020\u007f]/.test(value)) return null;
  if (/^\/(?!\/)/.test(value)) return { path: value, external: false };
  try {
    const url = new URL(value);
    return ['https:', 'http:', 'mailto:'].includes(url.protocol) && !url.username && !url.password
      ? { path: value, external: true } : null;
  } catch { return null; }
}

function ReferenceBrand({ name, logoUrl }) {
  const logo = safeBrandingUrl(logoUrl);
  const [failedImage, setFailedImage] = useState(null);
  const identity = `${name}:${logo}`;
  return logo && failedImage !== identity
    ? <img src={logo} alt={`${name} logo`} onError={() => setFailedImage(identity)} />
    : <span>{name}</span>;
}

// Presentation only: the caller supplies its resolved, authorized menu and
// real balance. This shell does not fetch data or add customer navigation.
export default function CustomerReferenceShell({
  brand = 'SMM Panel', logoUrl, brandPath = '/', navItems = [], balance, accountPath,
  onLogout, title, subtitle, actions, announcement, children, languageControl, wide = false,
  navigationLabel = 'Main navigation', menuLabel = 'Toggle navigation',
  skipLabel = 'Skip to content', accountLabel = 'Account', logoutLabel = 'Logout', balanceLabel = 'Balance',
}) {
  const { pathname, search } = useLocation();
  const navigationId = useId();
  const contentId = useId();
  const toggle = useRef(null);
  const content = useRef(null);
  const [mobile, setMobile] = useState(() => Boolean(window.matchMedia?.('(max-width: 1279px)').matches));
  const [open, setOpen] = useState(false);
  const brandTarget = navigationTarget(brandPath);
  const accountTarget = navigationTarget(accountPath);

  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 1279px)');
    if (!query) return undefined;
    const update = () => { setMobile(query.matches); setOpen(false); };
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname, search]);
  useEffect(() => {
    if (!mobile || !open) return undefined;
    const dismiss = event => {
      if (event.key === 'Escape') { setOpen(false); toggle.current?.focus(); }
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [mobile, open]);

  const close = () => setOpen(false);
  const link = (target, props, contents) => target.external
    ? <a {...props} href={target.path} rel="noopener noreferrer" onClick={close}>{contents}</a>
    : <Link {...props} to={target.path} onClick={close}>{contents}</Link>;
  const brandContent = <ReferenceBrand name={brand} logoUrl={logoUrl} />;

  return <div className="panel-shell customer-reference-shell" data-testid="customer-reference-shell">
    <a className="customer-reference-skip" href={`#${contentId}`} onClick={() => content.current?.focus()}>{skipLabel}</a>
    <header className="customer-reference-header">
      <div className="customer-reference-header-inner">
        {brandTarget ? link(brandTarget, { className: 'customer-reference-brand' }, brandContent)
          : <span className="customer-reference-brand">{brandContent}</span>}
        {languageControl && <div className="customer-reference-language">{languageControl}</div>}
        <button className="customer-reference-toggle" ref={toggle} type="button" hidden={!mobile}
          aria-label={menuLabel} aria-controls={navigationId} aria-expanded={open}
          onClick={() => setOpen(value => !value)}>
          <span aria-hidden="true"><span /><span /><span /></span>
        </button>
        <nav id={navigationId} className="customer-reference-navigation" aria-label={navigationLabel} hidden={mobile && !open}>
          <ul className="customer-reference-links">
            {navItems.map((item, index) => {
              const target = navigationTarget(item.path);
              if (!target) return null;
              const activePath = target.path.split(/[?#]/, 1)[0];
              const active = item.active !== undefined ? item.active : !target.external && (
                pathname === activePath || (!item.exact && activePath !== '/' && pathname.startsWith(`${activePath}/`)));
              return <li key={item.key || `${item.path}-${index}`}>
                {link(target, { className: `customer-reference-link${active ? ' is-active' : ''}`,
                  'aria-current': active ? 'page' : undefined }, item.label)}
              </li>;
            })}
          </ul>
          <div className="customer-reference-account">
            {balance !== undefined && balance !== null && <span className="customer-reference-balance" aria-label={`${balanceLabel}: ${balance}`}>{balance}</span>}
            {accountTarget && link(accountTarget, { className: 'customer-reference-link',
              'aria-current': !accountTarget.external && pathname === accountTarget.path ? 'page' : undefined }, accountLabel)}
            {typeof onLogout === 'function' && <button className="customer-reference-link" type="button" onClick={() => { close(); onLogout(); }}>{logoutLabel}</button>}
          </div>
        </nav>
      </div>
    </header>
    <main className={`customer-reference-content${wide ? ' customer-reference-content--wide' : ''}`} id={contentId} ref={content} tabIndex={-1}>
      {announcement && <div className="customer-reference-announcement">{announcement}</div>}
      {(title || subtitle || actions) && <div className="customer-reference-page-heading">
        <div>{title && <h1>{title}</h1>}{subtitle && <p>{subtitle}</p>}</div>
        {actions && <div className="customer-reference-page-actions">{actions}</div>}
      </div>}
      {children}
    </main>
  </div>;
}
